import { useEffect, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import SORT_OPTIONS from '@/shared/constants/centersSortOptions';
import { queryKeys } from '@/shared/constants/queryKeys';
import useBowlingCentersStore from '@/shared/store/bowlingCenters';

import { getBanner } from '@/services/home';

import useSearchParamsManager from '../useSearchParamsManager';
import useBowlingCentersData from './useBowlingCentersData';
import useGeoLocation from './useGeoLocation';

// Custom hook that holds the logic in the Home page
function useHome() {
  const { query, orderCriteria, updateSearchParams } = useSearchParamsManager({
    defaultOrderCriteria: {
      order: SORT_OPTIONS.GAMES,
      sort: 'desc',
    },
  });
  const { onRequestPermission, permissionDenied, location } = useGeoLocation();
  const { bowlingCenters } = useBowlingCentersData({
    location,
  });
  const { recents, updateRecents } = useBowlingCentersStore();
  const sortOptionsRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState(query);
  // update onSearch in useBowlingCentersData
  // but after a certain time
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm.length > 3) {
        // scroll to sort options
        sortOptionsRef.current.scrollIntoView({ behavior: 'smooth' });

        const shouldChangeOrder =
          orderCriteria.order === SORT_OPTIONS.PROXIMITY &&
          !location &&
          searchTerm !== '';

        if (shouldChangeOrder) {
          return updateSearchParams({
            order: SORT_OPTIONS.GAMES,
            sort: 'asc',
            q: searchTerm,
          });
        }

        updateSearchParams({ q: searchTerm });
      } else if (searchTerm.length <= 3) {
        updateSearchParams({
          q: '',
          ...orderCriteria,
        });
      }
    }, 500);

    return () => {
      clearTimeout(handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const { data: advertisement } = useQuery({
    queryKey: [queryKeys.banner],
    queryFn: getBanner,
    select: ({ data }) => data[0],
  });

  // side effect to update the list of recents bowling centers visited
  // with the updated list of bowling centers
  useEffect(() => {
    if (!bowlingCenters.data) return;
    // if there is already a center in recents list, update the data
    const updatedRecents = recents.map((recentCenter) => {
      const index = bowlingCenters.data.findIndex(
        (c) => c.uuid === recentCenter.uuid
      );
      if (index !== -1) {
        return bowlingCenters.data[index];
      }
      return recentCenter;
    });

    updateRecents(updatedRecents);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bowlingCenters.data]);

  return {
    bowlingCenters,
    orderCriteria,
    searchTerm,
    onSort: updateSearchParams,
    onSearch: setSearchTerm,
    advertisement,
    locationPermissionDenied: permissionDenied,
    onRequestPermission,
    showPermissionUi:
      orderCriteria.order === SORT_OPTIONS.PROXIMITY && !location,
    sortOptionsRef,
  };
}

export default useHome;
